/**
 * nt-geo.js — NTGeo: single source of truth for geo-aware shipping facts.
 *
 * Owns ALL cutoff/delivery-date math for the storefront (replaces the moment.js
 * pipeline that lived in snippets/countdown.liquid) plus visitor zone resolution
 * (ZIP -> zip3 -> zone) against the config island rendered by
 * snippets/nt-geo-config.liquid.
 *
 * CONTRACTS (do not break):
 * - snippets/countdown.liquid calls NTGeo.headerCountdown() and subscribes for
 *   re-renders. Its downstream consumers (ss-delivery-timer, __productCountDown,
 *   mobile-rotating-bar, bysize-v4 integrations.js) scrape the DOM it renders —
 *   date strings here must match moment's "MMM. D" / "ddd, MMM. D" output
 *   exactly ("Sep." not "Sept.", comma after weekday).
 * - The schedule matrix in computeHeaderSchedule() is a VERBATIM port of the
 *   legacy countdown.liquid weekday branches, including the three
 *   Monday-holiday special cases and the Sat-holiday +3d legacy safety rule.
 *   Quirks are intentional — parity with live behavior is the spec. Change
 *   business rules via config, not by "fixing" this function.
 * - localStorage key `deliveryPostalCode` is shared with ~15 legacy readers
 *   (blanks delivery estimate stack). We read/write it as the ZIP of record;
 *   `ntGeoZipMeta` ({ts, source}) is ours alone.
 * - No resolved geo value is ever rendered by Liquid (page-cache + Yottaa
 *   safety). Everything visitor-specific happens client-side here.
 *
 * Cache-safety: the #nt-geo-config island is identical for every visitor.
 */
(function () {
  'use strict';
  if (window.NTGeo) return;

  /* ------------------------------------------------------------------ *
   * Config
   * ------------------------------------------------------------------ */

  var FALLBACK_CFG = {
    version: 0,
    defaultZone: 'national',
    saturdayDelivery: true,
    holidays: [],
    provider: { endpoint: null, timeoutMs: 1200 },
    zipTtlDays: 30,
    zones: {
      national: {
        label: 'US',
        tz: 'America/New_York',
        cutoffs: { 'default': 13 },
        rates: { ground: null, express: null },
        transit: { ground: null, express: 1 },
        templates: {}
      }
    },
    zip3: {},
    zip5: {}
  };

  function readConfig() {
    var el = document.getElementById('nt-geo-config');
    if (!el) return FALLBACK_CFG;
    var cfg;
    try {
      cfg = JSON.parse(el.textContent);
    } catch (e) {
      try { console.warn('[nt-geo] config island unparseable, using fallback', e); } catch (_) {}
      return FALLBACK_CFG;
    }
    return validateConfig(cfg);
  }

  // A bad admin save must degrade to national, never break the page.
  function validateConfig(cfg) {
    if (!cfg || typeof cfg !== 'object' || !cfg.zones || typeof cfg.zones !== 'object') {
      try { console.warn('[nt-geo] config missing zones, using fallback'); } catch (_) {}
      return FALLBACK_CFG;
    }
    var dz = cfg.defaultZone || 'national';
    var zoneOk = function (z) {
      if (!z || typeof z.tz !== 'string' || !z.cutoffs) return false;
      var d = z.cutoffs['default'];
      // number = one hour every day; object = per-day {mon..sun: hour|null}
      return typeof d === 'number' || (d !== null && typeof d === 'object');
    };
    if (!zoneOk(cfg.zones[dz])) {
      try { console.warn('[nt-geo] default zone "' + dz + '" invalid, using fallback'); } catch (_) {}
      return FALLBACK_CFG;
    }
    // Drop (don't fatal) broken non-default zones and zip3 entries pointing nowhere.
    var zones = {};
    for (var k in cfg.zones) {
      if (!Object.prototype.hasOwnProperty.call(cfg.zones, k)) continue;
      if (zoneOk(cfg.zones[k])) {
        zones[k] = cfg.zones[k];
      } else {
        try { console.warn('[nt-geo] dropping invalid zone "' + k + '"'); } catch (_) {}
      }
    }
    var zip3 = {};
    var rawZip3 = cfg.zip3 || {};
    for (var p in rawZip3) {
      if (!Object.prototype.hasOwnProperty.call(rawZip3, p)) continue;
      if (zones[rawZip3[p]]) zip3[p] = rawZip3[p];
    }
    var zip5 = {};
    var rawZip5 = cfg.zip5 || {};
    for (var z5 in rawZip5) {
      if (!Object.prototype.hasOwnProperty.call(rawZip5, z5)) continue;
      if (zones[rawZip5[z5]]) zip5[z5] = rawZip5[z5];
    }
    return {
      version: cfg.version || 0,
      defaultZone: dz,
      saturdayDelivery: cfg.saturdayDelivery !== false,
      holidays: Array.isArray(cfg.holidays) ? cfg.holidays : [],
      provider: cfg.provider && typeof cfg.provider === 'object'
        ? { endpoint: cfg.provider.endpoint || null, timeoutMs: cfg.provider.timeoutMs || 1200 }
        : { endpoint: null, timeoutMs: 1200 },
      zipTtlDays: cfg.zipTtlDays || 30,
      zones: zones,
      zip3: zip3,
      zip5: zip5
    };
  }

  var CFG = readConfig();

  /* ------------------------------------------------------------------ *
   * Intl timezone kit — no getTimezoneOffset arithmetic, ever.
   * ------------------------------------------------------------------ */

  var DOW = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  var DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  var dtfCache = {};
  function getDTF(tz) {
    if (!dtfCache[tz]) {
      dtfCache[tz] = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hourCycle: 'h23',
        weekday: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }
    return dtfCache[tz];
  }

  // Wall-clock parts of an instant in a zone.
  function zoneParts(epochMs, tz) {
    var parts = getDTF(tz).formatToParts(epochMs);
    var o = {};
    for (var i = 0; i < parts.length; i++) o[parts[i].type] = parts[i].value;
    return {
      y: +o.year, m: +o.month, d: +o.day,
      hh: (+o.hour) % 24, mm: +o.minute, ss: +o.second,
      dow: DOW[o.weekday]
    };
  }

  // Wall clock -> instant: two-pass fixed point, converges across DST gaps/overlaps.
  function wallToEpoch(y, m, d, hh, mm, ss, tz) {
    var want = Date.UTC(y, m - 1, d, hh, mm, ss);
    var guess = want;
    for (var i = 0; i < 3; i++) {
      var p = zoneParts(guess, tz);
      var have = Date.UTC(p.y, p.m - 1, p.d, p.hh, p.mm, p.ss);
      if (have === want) return guess;
      guess += (want - have);
    }
    return guess;
  }

  // Pure calendar-date helpers (zone-independent facts about a Y-M-D).
  function addDays(dt, n) {
    var x = new Date(Date.UTC(dt.y, dt.m - 1, dt.d + n));
    return { y: x.getUTCFullYear(), m: x.getUTCMonth() + 1, d: x.getUTCDate() };
  }
  function dowOf(dt) {
    return new Date(Date.UTC(dt.y, dt.m - 1, dt.d)).getUTCDay();
  }
  function pad2(n) { return n < 10 ? '0' + n : '' + n; }
  function isoOf(dt) { return dt.y + '-' + pad2(dt.m) + '-' + pad2(dt.d); }

  function fmtShort(dt) { return MONTHS[dt.m - 1] + '. ' + dt.d; }           // moment "MMM. D"
  function fmtLabel(dt) { return DAYS[dowOf(dt)] + ', ' + fmtShort(dt); }    // moment "ddd, MMM. D"

  // Business-day helpers for the OFFER/tier math (deliveryFor). These are
  // deliberately separate from computeHeaderSchedule's internal closures —
  // that function is a verbatim legacy port proven by the parity gate and
  // must not be touched; these serve the new tier-based delivery promises.
  function isWorkingDt(dt, satOk) {
    var d = dowOf(dt);
    if (d === 0) return false;
    if (d === 6) return !!satOk;
    return true;
  }
  function isHolidayDt(dt, holidays) { return holidays.indexOf(isoOf(dt)) > -1; }
  function addBusinessDays(dt, n, satOk, holidays) {
    var x = dt;
    while (n > 0) {
      x = addDays(x, 1);
      if (isWorkingDt(x, satOk) && !isHolidayDt(x, holidays)) n--;
    }
    return x;
  }

  /* ------------------------------------------------------------------ *
   * Per-day cutoff schedule. A zone cutoff value is EITHER a single hour
   * (number — handled by the verbatim legacy matrix above, parity-proven)
   * OR a per-day object {mon..sun: hour|null} where null = closed that day.
   * Closed days and holidays roll forward to the next open day — the same
   * semantics the legacy weekend rolls encode, generalized. Owen 8-12:
   * "times may change by day of the week."
   * ------------------------------------------------------------------ */
  var DAYKEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

  function computePerDaySchedule(nowMs, opts /* {tz, hours, holidays, saturdayDelivery, headerTransit} */) {
    var tz = opts.tz;
    var holidays = opts.holidays || [];
    var now = zoneParts(nowMs, tz);
    var today = { y: now.y, m: now.m, d: now.d };

    // Next open cutoff instant: scan forward (today counts if its cutoff
    // hasn't passed). 21-day bound = safety against an all-closed config.
    var cutoffEpochMs = null, cutoffDay = null;
    for (var i = 0; i <= 21; i++) {
      var dt = addDays(today, i);
      if (isHolidayDt(dt, holidays)) continue;
      var h = opts.hours[DAYKEYS[dowOf(dt)]];
      if (h === null || h === undefined) continue;
      var epoch = wallToEpoch(dt.y, dt.m, dt.d, h, 0, 0, tz);
      if (epoch > nowMs) { cutoffEpochMs = epoch; cutoffDay = dt; break; }
    }
    if (cutoffEpochMs === null) {
      // Degenerate config — behave like a far-future cutoff on today.
      cutoffDay = today;
      cutoffEpochMs = nowMs + 21 * 86400000;
    }

    // Delivery promise: ship on the cutoff day, arrive after the fastest
    // configured transit (header shows the "Fastest Delivery" date).
    var transit = typeof opts.headerTransit === 'number' ? opts.headerTransit : 1;
    var delivery = addBusinessDays(cutoffDay, transit, opts.saturdayDelivery !== false, holidays);

    // Copy channel: legacy semantics — before a day's cutoff you see the
    // PREVIOUS weekday's channel; channels only exist Mon-Fri, so weekend
    // days map to Fri (same as the legacy matrix).
    var prev = addDays(cutoffDay, -1);
    var chDow = dowOf(prev);
    var channel = (chDow === 0 || chDow === 6) ? 'Fri' : DAYS[chDow];

    return {
      cutoffEpochMs: cutoffEpochMs,
      deliveryShort: fmtShort(delivery),
      deliveryLabel: fmtLabel(delivery),
      deliveryISO: isoOf(delivery),
      channel: channel,
      sundayProductOverride: false
    };
  }

  /* ------------------------------------------------------------------ *
   * Schedule engine — VERBATIM port of countdown.liquid's weekday matrix.
   * Reference: legacy lines 21-147 (moment pipeline). Quirks preserved:
   * - after-cutoff cutoff instants are built by ABSOLUTE ms addition from
   *   "now" (mints/hours-left-today), so they carry the current seconds and
   *   drift by an hour across a DST transition day — exactly like moment's
   *   .add(h,'hours'). Do not "clean up" to wall-clock setting.
   * - before-cutoff / Sat / Sun cutoffs are midnight + N absolute hours.
   * - the Sat-holiday +3d "legacy safety" rule runs BEFORE the generic
   *   bump-to-next-working-day, matching legacy line 134.
   * ------------------------------------------------------------------ */

  function computeHeaderSchedule(nowMs, opts) {
    var tz = opts.tz;
    var cutoffHour = opts.cutoffHour;
    var holidays = opts.holidays || [];
    var satWorking = opts.saturdayDelivery !== false;

    var now = zoneParts(nowMs, tz);
    var todayMidnight = wallToEpoch(now.y, now.m, now.d, 0, 0, 0, tz);
    var today = { y: now.y, m: now.m, d: now.d };

    function isHol(dt) { return holidays.indexOf(isoOf(dt)) > -1; }
    function isWorking(dt) {
      var d = dowOf(dt);
      if (d === 0) return false;
      if (d === 6) return satWorking;
      return true;
    }
    function bumpToNextWorkingDay(dt) {
      var x = dt;
      while (!isWorking(x) || isHol(x)) x = addDays(x, 1);
      return x;
    }
    function getNextMonday(dt) {
      var x = dt;
      while (dowOf(x) !== 1) x = addDays(x, 1);
      return x;
    }

    // legacy: mints_leftForEndingToday = 60 - min; hours_leftForEndingToday = 23 - hour;
    // eventDateTime = now + mints + hours + extra  (pure duration addition)
    function afterCutoffEpoch(extraHours) {
      return nowMs + (60 - now.mm) * 60000 + (23 - now.hh) * 3600000 + extraHours * 3600000;
    }
    // legacy: startOf('day').add(h,'hours') — absolute hours from midnight
    function fromMidnight(hours) { return todayMidnight + hours * 3600000; }

    var FORCE_AFTER_CUTOFF = isHol(today);
    var beforeCutoff = !FORCE_AFTER_CUTOFF && now.hh < cutoffHour;
    var dow = now.dow;

    var cutoffEpochMs, delivery, channel, sundayProductOverride = false;

    if (dow >= 1 && dow <= 4) { // Mon..Thu
      var prevChannel = ['', 'Fri', 'Mon', 'Tue', 'Wed'][dow]; // Mon->Fri, Tue->Mon, Wed->Tue, Thu->Wed
      var ownChannel = ['', 'Mon', 'Tue', 'Wed', 'Thu'][dow];
      if (beforeCutoff) {
        cutoffEpochMs = fromMidnight(cutoffHour);
        channel = prevChannel;
        delivery = addDays(today, 1);
      } else {
        cutoffEpochMs = afterCutoffEpoch(cutoffHour);
        channel = ownChannel;
        delivery = addDays(today, 2);
      }
    } else if (dow === 5) { // Fri
      if (beforeCutoff) {
        cutoffEpochMs = fromMidnight(cutoffHour);
        channel = 'Thu';
        delivery = addDays(today, 1); // Saturday
      } else {
        cutoffEpochMs = afterCutoffEpoch(47 + cutoffHour);
        channel = 'Fri';
        delivery = addDays(today, 4); // Tue
        // legacy SPECIAL CASE: Fri after cutoff + Monday holiday => +1 (no Sat pickup)
        if (isHol(getNextMonday(today))) delivery = addDays(delivery, 1);
      }
    } else if (dow === 6) { // Sat — single branch
      cutoffEpochMs = fromMidnight(47 + cutoffHour);
      channel = 'Fri';
      delivery = addDays(today, 3); // Tue
      if (isHol(getNextMonday(today))) delivery = addDays(delivery, 1);
    } else { // Sun — single branch
      cutoffEpochMs = fromMidnight(23 + cutoffHour);
      channel = 'Fri';
      sundayProductOverride = true;
      delivery = addDays(today, 2);
      if (isHol(getNextMonday(today))) delivery = addDays(delivery, 1);
    }

    // legacy delivery normalize (lines 132-138)
    if (dowOf(delivery) === 6 && isHol(delivery)) delivery = addDays(delivery, 3); // legacy safety
    delivery = bumpToNextWorkingDay(delivery);

    // legacy cutoff normalize (lines 140-147): cutoff landing on a
    // non-working/holiday day rolls to the next working day AT the cutoff hour.
    var evt = zoneParts(cutoffEpochMs, tz);
    var evtDate = { y: evt.y, m: evt.m, d: evt.d };
    if (!isWorking(evtDate) || isHol(evtDate)) {
      var nextWork = bumpToNextWorkingDay(addDays(evtDate, 1));
      cutoffEpochMs = wallToEpoch(nextWork.y, nextWork.m, nextWork.d, cutoffHour, 0, 0, tz);
    }

    return {
      cutoffEpochMs: cutoffEpochMs,
      deliveryShort: fmtShort(delivery),   // -> window.getEvtDate ("Aug. 12")
      deliveryLabel: fmtLabel(delivery),   // -> <date> body ("Wed, Aug. 12")
      deliveryISO: isoOf(delivery),
      channel: channel,
      sundayProductOverride: sundayProductOverride
    };
  }

  /* ------------------------------------------------------------------ *
   * Analytics — guarded gtag, matches assets/multiupload-ga-tracking.js
   * ------------------------------------------------------------------ */

  function environment() {
    return (window.Shopify && window.Shopify.theme && window.Shopify.theme.role === 'main')
      ? 'production' : 'staging';
  }
  function deviceType() { return window.innerWidth <= 767 ? 'mobile' : 'desktop'; }
  // First-party interaction counter (Owen 8-21: reporting must not lean on
  // GA4). Selected events also tick a CloudWatch metric via our own /evt
  // endpoint — the denominator for the app's ZIP->purchase conversion rate.
  // Once per session per event. sendBeacon is fire-and-forget by browser
  // design: non-blocking, no response handling, cannot throw into the page.
  // Runs BEFORE the gtag check on purpose — ad-blocked shoppers (no gtag)
  // must still count. Direct API GW URL on purpose: the CloudFront
  // distribution's edge function answers every path it sees geo headers on,
  // so /evt through it would never reach the counter.
  var EVT_URL = 'https://6g3tcmpgkc.execute-api.us-east-1.amazonaws.com/evt';
  function beacon(name, params) {
    try {
      var e = null;
      if (name === 'nt_geo_resolved') e = 'session'; // one per session — the conversion-rate denominator
      else if (name === 'nt_geo_zip_opened') e = 'zip_opened';
      else if (name === 'nt_geo_zip_set') e = (params && params.zip_source === 'gps') ? 'zip_set_gps' : 'zip_set_manual';
      if (!e) return;
      var k = 'ntGeoEvt_' + e;
      if (sessionStorage.getItem(k)) return;
      sessionStorage.setItem(k, '1');
      var body = JSON.stringify({ e: e });
      if (navigator.sendBeacon) navigator.sendBeacon(EVT_URL, body);
      else fetch(EVT_URL, { method: 'POST', body: body, keepalive: true }).catch(function () {});
    } catch (x) { /* counting must never break the page */ }
  }

  // zip_change — the from->to detail beacon (analytics spec, Cal/Owen 8-25).
  // Unlike the once-per-session counters above, EVERY user-initiated ZIP
  // change reports: the payload (which ZIP they started with, how we knew it,
  // what they changed it to) IS the data, not the count. Lands in the evt
  // Lambda's log -> warehouse. Same fire-and-forget rules; ZIPs only, no PII.
  // User actions only (manual/GPS) — background account/ip re-resolutions
  // are deliberately not "changes".
  function beaconChange(prev, next, method) {
    try {
      if (!next || !next.zip) return;
      if (prev && prev.zip === next.zip) return; // same ZIP re-entered — not a change
      // Remember what this change REPLACED (persisted in our meta, survives
      // page loads) so the cart-note writer can stamp orders with
      // _nt_geo_prev_zip — the key that joins error-distance to conversion
      // (Scott 8-26). writeStoredZip already rewrote META for the new ZIP;
      // merge, never replace, so ts/source stay intact.
      try {
        var m = JSON.parse(localStorage.getItem(META_KEY) || '{}') || {};
        m.prev_zip = (prev && prev.zip) || null;
        m.prev_src = (prev && prev.source) || 'none';
        localStorage.setItem(META_KEY, JSON.stringify(m));
      } catch (pm) { /* storage blocked — beacon still fires */ }
      var body = JSON.stringify({
        e: 'zip_change',
        from_zip: (prev && prev.zip) || null,
        from_src: (prev && prev.source) || 'none',
        to_zip: next.zip,
        method: method,
        zone_from: (prev && prev.zone) || null,
        zone_to: next.zone
      });
      if (navigator.sendBeacon) navigator.sendBeacon(EVT_URL, body);
      else fetch(EVT_URL, { method: 'POST', body: body, keepalive: true }).catch(function () {});
    } catch (x) { /* counting must never break the page */ }
  }

  function fireGA(name, params) {
    beacon(name, params);
    try {
      if (typeof gtag === 'undefined') return;
      params = params || {};
      params.environment = environment();
      params.device_type = deviceType();
      gtag('event', name, params);
    } catch (e) { /* analytics must never break the page */ }
  }
  // USER-level segmentation (Owen 8-17): stamp the resolved zone + how we
  // know it as GA4 user properties, so EVERY report (revenue, conversion,
  // product views) can be sliced by zone — not just the nt_geo_* events.
  // Re-stamped on every resolution; GA keeps the latest value per user.
  function stampUserProps(state) {
    try {
      if (typeof gtag === 'undefined' || !state) return;
      gtag('set', 'user_properties', {
        geo_zone: state.zone || 'national',
        geo_source: state.source || 'default'
      });
    } catch (e) { /* analytics must never break the page */ }
  }

  /* ------------------------------------------------------------------ *
   * Resolver — one resolution per page load.
   * Priority: stored ZIP (manual/legacy) -> customer address (v1 stub)
   *           -> cart address (v1 stub) -> IP provider -> default zone.
   * ------------------------------------------------------------------ */

  var ZIP_KEY = 'deliveryPostalCode'; // SHARED with the legacy blanks estimate stack
  var META_KEY = 'ntGeoZipMeta';      // ours: {ts, source}

  function readStoredZip() {
    var zip, meta = null;
    try {
      zip = localStorage.getItem(ZIP_KEY);
      var rawMeta = localStorage.getItem(META_KEY);
      if (rawMeta) meta = JSON.parse(rawMeta);
    } catch (e) { return null; }
    if (!zip || !/^\d{5}$/.test(zip)) return null;
    if (meta && typeof meta.ts === 'number') {
      var src = meta.source || 'stored';
      // Expiration policy (Owen 8-12): a ZIP the CUSTOMER set (typed in the
      // chip, or "Use my location") NEVER expires — only editing the chip
      // replaces it. IP guesses keep a SLIDING zipTtlDays window: each visit
      // that uses the guess refreshes its clock, so it only expires after
      // zipTtlDays of absence, then we simply re-guess.
      var permanent = src === 'manual' || src === 'gps';
      if (!permanent) {
        var ttlMs = CFG.zipTtlDays * 24 * 3600000;
        if (Date.now() - meta.ts > ttlMs) return null; // expired — re-resolve
        try {
          localStorage.setItem(META_KEY, JSON.stringify({ ts: Date.now(), source: src }));
        } catch (e) { /* touch is best-effort */ }
      }
      return { zip: zip, source: src };
    }
    // Bare legacy ZIP written by the old stack — honor it, never fight it.
    return { zip: zip, source: 'legacy' };
  }

  function writeStoredZip(zip, source) {
    try {
      localStorage.setItem(ZIP_KEY, zip);
      var meta = { ts: Date.now(), source: source };
      // Carry the prev_zip/prev_src fields forward — every write path calls
      // this (incl. same-ZIP re-entry and background account overrides), and
      // only a REAL user change (beaconChange) may rewrite what "prev" means.
      try {
        var old = JSON.parse(localStorage.getItem(META_KEY) || '{}') || {};
        if (old.prev_zip) { meta.prev_zip = old.prev_zip; meta.prev_src = old.prev_src; }
      } catch (om) { /* unreadable old meta — fresh meta is fine */ }
      localStorage.setItem(META_KEY, JSON.stringify(meta));
    } catch (e) { /* storage full/blocked — session-only state still works */ }
  }

  // Rank 2 (wired 8-17, after the customer-address leak gate PASSED):
  // logged-in customers resolve from their saved default address via a
  // client-side Section Rendering API fetch of sections/nt-geo-customer.
  // NEVER move this into full-page Liquid — the SRA fetch is per-session;
  // page HTML is shared-cached. Detection rides Shopify's own analytics
  // customer id (set inline in <head>, present before this deferred module).
  // Plain promise, no timeout — callers add their own race when they need
  // one, and background refreshes want the untimed answer.
  function customerAddressAdapter() {
    var cid = null;
    try {
      cid = (window.__st && window.__st.cid) ||
        (window.ShopifyAnalytics && window.ShopifyAnalytics.meta &&
         window.ShopifyAnalytics.meta.page && window.ShopifyAnalytics.meta.page.customerId);
    } catch (e) { /* fall through */ }
    if (!cid || typeof fetch !== 'function') return Promise.resolve(null);
    var started = Date.now();
    return fetch('/?sections=nt-geo-customer', { credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (map) {
        var html = map && map['nt-geo-customer'];
        if (!html) return null;
        var m = /data-nt-geo-customer>([\s\S]*?)<\/script>/.exec(html);
        if (!m) return null;
        var zip = JSON.parse(m[1]).zip;
        // Saved addresses carry ZIP+4 ("19154-3201") — quote off the zip5.
        var five = zip && String(zip).match(/^\d{5}/);
        if (!five) return null;
        return { zip: five[0], source: 'account', latency: Date.now() - started };
      })
      .catch(function () { return null; });
  }
  function cartAddressAdapter() { return Promise.resolve(null); }

  // Rank 4: IP provider via our geo endpoint. Must never throw; null on any
  // failure. SOFT timeout: past timeoutMs we resolve null so the page shows
  // the default immediately (never a hung skeleton), but the request keeps
  // running — a late answer is delivered via onLate so the chip still fills
  // in on THIS page view (and is stored, making every later view instant).
  function ipAdapter(onLate) {
    var endpoint = CFG.provider.endpoint;
    if (!endpoint || typeof fetch !== 'function') return Promise.resolve(null);
    var started = Date.now();
    var settled = false;

    // The config snippet fires this request from an inline HEAD script long
    // before this deferred module runs — consume the in-flight response
    // instead of starting a second one (landing-speed path).
    var inflight = window.__ntGeoPrefetch;
    window.__ntGeoPrefetch = null;
    var request = (inflight && typeof inflight.then === 'function' ? inflight : fetch(endpoint))
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        if (!data || !data.zip || !/^\d{5}$/.test(String(data.zip))) {
          fireGA('nt_geo_provider_null', { latency_ms: Date.now() - started });
          return null;
        }
        if (data.country && data.country !== 'US') return null;
        return { zip: String(data.zip), source: 'ip', latency: Date.now() - started };
      })
      .catch(function () {
        fireGA('nt_geo_provider_null', { latency_ms: Date.now() - started });
        return null;
      });

    request.then(function (r) {
      if (settled && r && typeof onLate === 'function') onLate(r);
    });

    // If the timer fires first, `settled` marks that the page already moved
    // on with the default — the request.then above then hands any eventual
    // answer to onLate. If the request finishes first, its .then has already
    // run (settled still false) and no late delivery happens.
    return Promise.race([
      request,
      new Promise(function (resolve) {
        setTimeout(function () { settled = true; resolve(null); }, CFG.provider.timeoutMs);
      })
    ]);
  }

  /* ------------------------------------------------------------------ *
   * State + pub/sub
   * ------------------------------------------------------------------ */

  // Full-ZIP entries (zip5) win over prefix entries (zip3) — zip5 exists so
  // a zone can EXACTLY mirror the checkout app's ZIP list; zip3 stays for
  // compact broad strokes (Owen 8-12).
  function zoneForZip(zip) {
    if (!zip) return CFG.defaultZone;
    return CFG.zip5[zip] || CFG.zip3[zip.slice(0, 3)] || CFG.defaultZone;
  }

  function buildState(zip, source, resolved) {
    var zone = zoneForZip(zip);
    return {
      zip: zip || null,
      zip3: zip ? zip.slice(0, 3) : null,
      zone: zone,
      zoneCfg: CFG.zones[zone],
      source: source,
      resolved: resolved
    };
  }

  var state = null;
  var subscribers = [];
  var readyResolve;
  var ready = new Promise(function (res) { readyResolve = res; });

  function broadcast() {
    stampUserProps(state); // every state change refreshes the GA4 user props
    for (var i = 0; i < subscribers.length; i++) {
      try { subscribers[i](state); } catch (e) { /* one bad subscriber can't block the rest */ }
    }
    try {
      document.dispatchEvent(new CustomEvent('nt-geo:changed', { detail: state, bubbles: true }));
    } catch (e) { /* CustomEvent unsupported — subscribers above still ran */ }
  }

  /* ------------------------------------------------------------------ *
   * Public API
   * ------------------------------------------------------------------ */

  var NTGeo = {
    ready: ready,

    get: function () { return state; },

    set: function (zip) {
      zip = String(zip || '').trim();
      if (!/^\d{5}$/.test(zip)) return { ok: false, error: 'Enter a 5-digit US ZIP code' };
      var prev = state;
      writeStoredZip(zip, 'manual');
      state = buildState(zip, 'manual', true);
      beaconChange(prev, state, 'manual');
      fireGA('nt_geo_zip_set', { zone: state.zone });
      broadcast();
      return { ok: true, state: state };
    },

    clear: function () {
      try {
        localStorage.removeItem(ZIP_KEY);
        localStorage.removeItem(META_KEY);
      } catch (e) { /* ignore */ }
      state = buildState(null, 'default', true);
      broadcast();
    },

    subscribe: function (fn) {
      subscribers.push(fn);
      if (state) {
        try { fn(state); } catch (e) { /* subscriber error is theirs */ }
      }
      return function () {
        var i = subscribers.indexOf(fn);
        if (i > -1) subscribers.splice(i, 1);
      };
    },

    // Schedule for a cutoff type ('default' | 'dtf' | 'blanks' | ...) in the
    // visitor's zone. Adds msRemaining/passed relative to "now".
    // A cutoff value that is a NUMBER runs the parity-proven legacy matrix;
    // a per-day OBJECT ({mon..sun: hour|null}) runs the generalized scheduler.
    cutoffFor: function (type) {
      var z = state ? state.zoneCfg : CFG.zones[CFG.defaultZone];
      var raw = (z.cutoffs && z.cutoffs[type] !== undefined) ? z.cutoffs[type] : z.cutoffs['default'];
      var s;
      if (raw !== null && typeof raw === 'object') {
        var fastest = (z.transit && typeof z.transit['next-day'] === 'number') ? z.transit['next-day'] : 1;
        s = computePerDaySchedule(Date.now(), {
          tz: z.tz,
          hours: raw,
          holidays: CFG.holidays,
          saturdayDelivery: CFG.saturdayDelivery,
          headerTransit: fastest
        });
      } else {
        s = computeHeaderSchedule(Date.now(), {
          tz: z.tz,
          cutoffHour: typeof raw === 'number' ? raw : 13,
          holidays: CFG.holidays,
          saturdayDelivery: CFG.saturdayDelivery
        });
      }
      s.msRemaining = s.cutoffEpochMs - Date.now();
      s.passed = s.msRemaining <= 0;
      return s;
    },

    headerCountdown: function () { return NTGeo.cutoffFor('default'); },

    // Tier-based delivery date: ship date (the calendar day of the next
    // cutoff, already rolled to a working day by cutoffFor) plus the
    // method's transit in business days. Powers [DATE:method] placeholders.
    deliveryFor: function (method, type) {
      var z = state ? state.zoneCfg : CFG.zones[CFG.defaultZone];
      var transit = (z.transit && typeof z.transit[method] === 'number') ? z.transit[method] : null;
      if (transit === null) return null;
      var s = NTGeo.cutoffFor(type || 'default');
      var ship = zoneParts(s.cutoffEpochMs, z.tz);
      var shipDt = { y: ship.y, m: ship.m, d: ship.d };
      var del = addBusinessDays(shipDt, transit, CFG.saturdayDelivery, CFG.holidays);
      return { dateISO: isoOf(del), short: fmtShort(del), label: fmtLabel(del), transit: transit, method: method };
    },

    // Cart-total tier state for the zone's offers ladder.
    // -> { achieved: offer|null, next: offer|null, remainingCents }
    offerState: function (cartCents) {
      var z = state ? state.zoneCfg : CFG.zones[CFG.defaultZone];
      var offers = Array.isArray(z.offers) ? z.offers.slice().sort(function (a, b) { return a.min - b.min; }) : [];
      var achieved = null, next = null;
      for (var i = 0; i < offers.length; i++) {
        if (cartCents >= offers[i].min * 100) achieved = offers[i];
        else if (!next) next = offers[i];
      }
      return {
        achieved: achieved,
        next: next,
        remainingCents: next ? Math.max(0, Math.round(next.min * 100 - cartCents)) : 0
      };
    },

    rateFor: function (method) {
      var z = state ? state.zoneCfg : CFG.zones[CFG.defaultZone];
      return (z.rates && typeof z.rates[method] === 'number') ? z.rates[method] : null;
    },

    transitFor: function (method) {
      var z = state ? state.zoneCfg : CFG.zones[CFG.defaultZone];
      return (z.transit && typeof z.transit[method] === 'number') ? z.transit[method] : null;
    },

    templateFor: function (fact) {
      var z = state ? state.zoneCfg : CFG.zones[CFG.defaultZone];
      return (z.templates && z.templates[fact]) || null;
    },

    config: function () { return CFG; },

    // Guarded analytics passthrough so UI components don't duplicate the
    // gtag wrapper (no-parallel-logic rule).
    track: fireGA,

    // Browser GPS -> ZIP. Called ONLY from an explicit user gesture (the
    // "Use my location" button in the ZIP popover) so the permission prompt
    // is contextual, never ambient. Same reverse-geocode service the blanks
    // pages already use (component-blanks-home.js) — no new dependencies.
    // Honors the existing locationPermissionDenied convention.
    locate: function () {
      return new Promise(function (resolve) {
        if (!navigator.geolocation) { resolve({ ok: false, error: 'Location is not available in this browser' }); return; }
        navigator.geolocation.getCurrentPosition(function (pos) {
          // The position callback firing means the shopper GRANTED the
          // browser prompt (or had a standing grant) — count it before the
          // reverse-geocode, whose failures are a separate funnel step.
          fireGA('nt_geo_gps_granted', {});
          fetch('https://nominatim.openstreetmap.org/reverse?lat=' + pos.coords.latitude +
                '&lon=' + pos.coords.longitude + '&format=json')
            .then(function (r) { return r.json(); })
            .then(function (d) {
              var zip = d && d.address && String(d.address.postcode || '').slice(0, 5);
              if (zip && /^\d{5}$/.test(zip)) {
                var prev = state;
                writeStoredZip(zip, 'gps');
                state = buildState(zip, 'gps', true);
                beaconChange(prev, state, 'gps');
                fireGA('nt_geo_zip_set', { zone: state.zone, zip_source: 'gps' });
                broadcast();
                resolve({ ok: true, state: state });
              } else {
                resolve({ ok: false, error: "Couldn't find a ZIP for your location — type it instead" });
              }
            })
            .catch(function () { resolve({ ok: false, error: "Couldn't look up your location — type your ZIP instead" }); });
        }, function (err) {
          if (err && err.code === 1) {
            try { localStorage.setItem('locationPermissionDenied', 'true'); } catch (e) { /* ignore */ }
            fireGA('nt_geo_gps_denied', {});
            resolve({ ok: false, denied: true, error: 'Location permission declined — type your ZIP instead' });
          } else if (err && err.code === 2) {
            // Browser allowed, but the OS returned nothing — usually Location
            // Services disabled for the browser at the system level.
            resolve({ ok: false, error: 'Your device blocked the location (often Location Services in system settings) — type your ZIP instead' });
          } else {
            resolve({ ok: false, error: 'Location timed out — type your ZIP instead' });
          }
        }, { timeout: 8000, maximumAge: 600000 });
      });
    },

    locationDenied: function () {
      try { return localStorage.getItem('locationPermissionDenied') === 'true'; } catch (e) { return false; }
    },

    // What the shopper's last ZIP change replaced ({zip, src}) or null if they
    // never changed one. Read by nt-geo-ui's cart-note writer; persists until
    // the next change (or clear()). Extra META keys are ignored by
    // readStoredZip — additive, no migration.
    prevChange: function () {
      try {
        var m = JSON.parse(localStorage.getItem(META_KEY) || '{}') || {};
        return m.prev_zip ? { zip: m.prev_zip, src: m.prev_src || 'none' } : null;
      } catch (e) { return null; }
    },

    // Test hooks for the harnesses (dev/geo/*) — NOT public contracts.
    _schedule: computeHeaderSchedule,
    _perDaySchedule: computePerDaySchedule
  };

  /* ------------------------------------------------------------------ *
   * Boot — resolve once, synchronously where possible.
   * ------------------------------------------------------------------ */

  var bootStart = Date.now();

  // Landing-steer GA handoff: the homepage steer (nt-geo-config.liquid)
  // navigates away before a dataLayer push could ever be sent, so it stashes
  // the context in sessionStorage and THIS page reports the event on arrival.
  try {
    var steerEvt = sessionStorage.getItem('ntGeoSteerEvt');
    if (steerEvt) {
      sessionStorage.removeItem('ntGeoSteerEvt');
      var se = JSON.parse(steerEvt);
      fireGA('nt_geo_steered', { zone: se.zone, steer_via: se.via });
    }
  } catch (e) { /* storage blocked — skip the event, never the boot */ }

  var stored = readStoredZip();
  if (stored) {
    state = buildState(stored.zip, stored.source, true);
    fireGA('nt_geo_resolved', { zip_source: stored.source, zone: state.zone, latency_ms: 0 });
    stampUserProps(state); // boot short-circuit skips broadcast — stamp here
    readyResolve(state);
    // No broadcast needed at boot: subscribe() replays current state.
    // Rung-2 background refresh: a logged-in customer's saved address
    // outranks any GUESSED zip (ip/legacy) and keeps its own stored copy
    // fresh (a default-address edit shows up on the next visit). It never
    // touches a zip the customer SET here (manual/gps rank above it).
    if (stored.source === 'ip' || stored.source === 'legacy' || stored.source === 'account') {
      customerAddressAdapter().then(function (r) {
        if (!r) return;
        if (state.source === 'manual' || state.source === 'gps') return; // set while we fetched
        if (r.zip === state.zip) {
          // Same zip — just upgrade the stored provenance so future
          // refreshes keep running; nothing visible changed, no broadcast.
          if (state.source !== 'account') writeStoredZip(r.zip, 'account');
          return;
        }
        writeStoredZip(r.zip, 'account');
        state = buildState(r.zip, 'account', true);
        fireGA('nt_geo_resolved', { zip_source: 'account-refresh', zone: state.zone, latency_ms: r.latency || 0 });
        broadcast();
      });
    }
  } else {
    // Visible immediately as the default zone; upgrade async if an adapter hits.
    state = buildState(null, 'default', false);
    var applyLateIp = function (r) {
      // A slow first lookup landed after we already showed the default.
      // Apply it ONLY if nothing better arrived meanwhile (never clobber a
      // manual/GPS ZIP the visitor set while we waited).
      if (state.source !== 'default') return;
      writeStoredZip(r.zip, r.source);
      state = buildState(r.zip, r.source, true);
      fireGA('nt_geo_resolved', { zip_source: 'ip-late', zone: state.zone, latency_ms: r.latency || 0 });
      broadcast();
    };
    // Rung 2 rides the same soft-timeout pattern as the IP rung: past the
    // budget we move on (IP prefetch is usually already in flight), but a
    // late account answer still lands — and may OVERRIDE an ip/legacy guess,
    // because a saved address outranks a guess. Never a manual/gps zip.
    var applyLateAccount = function (r) {
      if (state.source !== 'default' && state.source !== 'ip' && state.source !== 'legacy') return;
      writeStoredZip(r.zip, r.source);
      state = buildState(r.zip, r.source, true);
      fireGA('nt_geo_resolved', { zip_source: 'account-late', zone: state.zone, latency_ms: r.latency || 0 });
      broadcast();
    };
    var accountSettled = false;
    var accountRequest = customerAddressAdapter();
    accountRequest.then(function (r) {
      if (accountSettled && r) applyLateAccount(r);
    });
    Promise.race([
      accountRequest,
      new Promise(function (resolve) {
        setTimeout(function () { accountSettled = true; resolve(null); }, CFG.provider.timeoutMs);
      })
    ])
      .then(function (r) { return r || cartAddressAdapter(); })
      .then(function (r) { return r || ipAdapter(applyLateIp); })
      .then(function (r) {
        if (r && r.zip) {
          writeStoredZip(r.zip, r.source);
          state = buildState(r.zip, r.source, true);
        } else if (state.source === 'default') {
          state = buildState(null, 'default', true);
        }
        fireGA('nt_geo_resolved', {
          zip_source: state.source, zone: state.zone, latency_ms: Date.now() - bootStart
        });
        readyResolve(state);
        broadcast();
      });
  }

  window.NTGeo = NTGeo;
}());
